import React, { useEffect, useState } from 'react'

import { HelpCircle as HelpCircleIcon } from 'react-feather'
import {
  Box,
  Button,
  Card,
  TablePagination,
  Typography,
  useMediaQuery,
} from '@material-ui/core'

import {
  BaseDialog,
  Container,
  ContentHeader,
  DriveTour,
  FilterButton,
  Page,
  Permitted,
} from 'components'
import {
  ComplaintFilters,
  ComplaintTable,
  ComplaintTwoFactorDialog,
} from './components'

import useFetch from 'hooks/useFetch'
import usePagination from 'hooks/usePagination'
import useFilter from 'hooks/useFilter'
import useAuth from 'hooks/useAuth'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import theme from 'theme'

const ComplaintMain = () => {
  const [isLoadingActiveComplaints, setIsLoadingActiveComplaints] =
    useState(false)
  const [openTour, setOpenTour] = useState(false)
  const [open, setOpen] = useState(false)

  const { company } = useAuth()
  const [activateComplaints, setActivateComplaints] = useState(
    company?.complaintActivated,
  )

  // 2FA states - simplificado
  const [twoFactorRequired, setTwoFactorRequired] = useState(false)
  const [twoFactorBlocked, setTwoFactorBlocked] = useState(false)
  const [twoFactorAccessToken, setTwoFactorAccessToken] = useState()
  const [twoFactorAuthToken, setTwoFactorAuthToken] = useState(() =>
    service.dponet.complaintTwoFactorStorage.getToken(),
  )

  const filter = useFilter()

  const { page, perPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination(10)
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })
  const snackbar = useSnackbar()

  const shouldFetchComplaints = !twoFactorRequired && !twoFactorBlocked

  const { response, isLoading } = useFetch(
    service.dponet.complaint.get,
    {
      page,
      perPage,
      ...filter.filters,
      twoFactorAuthToken,
    },
    [filter.filters, page, perPage, twoFactorAuthToken, shouldFetchComplaints],
    shouldFetchComplaints,
  )

  const complaints =
    twoFactorRequired || twoFactorBlocked ? [] : response?.data?.complaints

  useEffect(() => {
    if (!response) return

    if (response.status === 202) {
      if (!twoFactorRequired) {
        setTwoFactorAccessToken(response.data?.accessToken)
        setTwoFactorRequired(true)
      }
      return
    }

    if (response.status === 200) {
      // Sucesso - fecha modal e limpa bloqueio
      setTwoFactorRequired(false)
      setTwoFactorBlocked(false)
    }
  }, [response])

  const handleOpenTour = () => setOpenTour((prev) => !prev)

  const handleActivate = async () => {
    setIsLoadingActiveComplaints(true)
    const nextValue = !activateComplaints
    setActivateComplaints(nextValue)
    if (onActivateComplaints) {
      await onActivateComplaints(nextValue)
    }

    setOpen(false)
    setIsLoadingActiveComplaints(false)
  }

  const handleOpenDialog = () => setOpen(true)

  const onActivateComplaints = async (checked) => {
    try {
      await service.dponet.complaint.activateComplaints({
        complaint: {
          activateComplaints: checked,
        },
      })
      snackbar.open({
        message: `Canal de Denúncias ${
          checked ? 'ativado' : 'desativado'
        } com sucesso!`,
        variant: 'success',
      })
      setActivateComplaints(checked)
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error.response.data.error),
        variant: 'error',
      })
    } finally {
      setIsLoadingActiveComplaints(false)
    }
  }

  const handleTwoFactorSuccess = ({ authToken }) => {
    // Salva token e refaz a requisição automaticamente
    service.dponet.complaintTwoFactorStorage.setToken(authToken)
    setTwoFactorAuthToken(authToken)
    setTwoFactorAccessToken(undefined)
    setTwoFactorRequired(false)
    setTwoFactorBlocked(false)
  }

  const handleTwoFactorCancel = () => {
    // Fecha modal e bloqueia visualização até autenticação
    setTwoFactorRequired(false)
    setTwoFactorBlocked(true)
  }

  const handleRetryTwoFactor = () => {
    // Reabre a modal para tentar novamente
    setTwoFactorBlocked(false)
    setTwoFactorRequired(true)
  }

  return (
    <Page title="Canal de Denúncia">
      <Container maxWidth={false}>
        <ComplaintTwoFactorDialog
          accessToken={twoFactorAccessToken}
          onCancel={handleTwoFactorCancel}
          onSuccess={handleTwoFactorSuccess}
          open={twoFactorRequired}
        />
        <Box>
          <ContentHeader title="Canal de Denúncia">
            <Button
              variant="contained"
              color="primary"
              startIcon={<HelpCircleIcon size={20} />}
              onClick={handleOpenTour}
              id={constants.complaint.COMPLAINT_MAIN_DRIVER_STEP_0}
              fullWidth
            >
              Tutorial
            </Button>

            <Permitted
              tag={constants.permissions.COMPLAINTS.ACTIVATE_COMPLAINTS}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenDialog}
                fullWidth
              >
                {!activateComplaints ? 'Ativar' : 'Desativar'}
                &nbsp;Canal de Denúncias
              </Button>
            </Permitted>
            <FilterButton setDrawerOpen={filter.setDrawerOpen} />
          </ContentHeader>
        </Box>
        <Card>
          {twoFactorBlocked ? (
            <Box p={4} textAlign="center">
              <Typography variant="h6" color="textPrimary">
                A visualização das denúncias está bloqueada até que a dupla
                autenticação seja concluída.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Confirme o código enviado para o seu e-mail para liberar o
                acesso.
              </Typography>
              <Box mt={3} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRetryTwoFactor}
                >
                  Tentar novamente
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <ComplaintTable complaints={complaints} isLoading={isLoading} />
              <Box px={2} display="flex" justifyContent="flex-end">
                <TablePagination
                  component="div"
                  count={response?.data?.meta?.totalCount || 0}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  page={page - 1}
                  rowsPerPage={perPage}
                  rowsPerPageOptions={[5, 10, 25, 100]}
                  labelRowsPerPage={isDesktop ? 'Por página' : ''}
                  nextIconButtonProps={{ size: 'small' }}
                  backIconButtonProps={{ size: 'small' }}
                />
              </Box>
            </>
          )}
        </Card>
        <ComplaintFilters filter={filter} />
      </Container>

      <DriveTour
        stepsMatrix={constants.complaint.COMPLAINT_MAIN_DRIVER_STEPS_OBJECT}
        open={openTour}
        setOpen={setOpenTour}
      />

      <BaseDialog
        open={open}
        setOpen={setOpen}
        title={
          activateComplaints
            ? 'Desativar Canal de Denúncias'
            : 'Ativar Canal de Denúncias'
        }
        closeButtonText="Cancelar"
        actionButtonText={activateComplaints ? 'Desativar' : 'Ativar'}
        loading={isLoadingActiveComplaints}
        actionButton={handleActivate}
      >
        <Typography>
          O Canal de Denúncias é opcional, podendo optar por ativar ou não. Com
          isso, seu portal de privacidade passará a incluir uma opção para os
          titulares registrarem denúncias.
        </Typography>
      </BaseDialog>
    </Page>
  )
}

export default ComplaintMain
